/**
 * Visual treatment for a run-record outcome (icon + color class).
 *
 * The outcome string comes from the device firmware (`mapRunOutcome` on the BE).
 * Known values today: `Completed`, `CompletedWithWarnings`, `Aborted`, `Running`.
 * Other values are coerced via substring match so we degrade gracefully if the
 * firmware introduces a new state.
 */
export type RunRecordOutcomeIcon = 'event-notice' | 'event-warning' | 'event-error';

export interface RunRecordOutcomeAppearance {
  icon: RunRecordOutcomeIcon;
  /** Tailwind-style color class, used both for the icon background and outcome label. */
  colorClassName: string;
}

const KNOWN_OUTCOMES: Record<string, RunRecordOutcomeAppearance> = {
  Completed: { icon: 'event-notice', colorClassName: 'color-green-500' },
  CompletedWithWarnings: { icon: 'event-warning', colorClassName: 'color-orange-500' },
  Aborted: { icon: 'event-error', colorClassName: 'color-red-500' },
  Running: { icon: 'event-notice', colorClassName: 'color-blue-500' },
};

const FALLBACK: RunRecordOutcomeAppearance = {
  icon: 'event-notice',
  colorClassName: 'color-green-500',
};

export function getRunRecordOutcomeAppearance(
  outcome: string,
): RunRecordOutcomeAppearance {
  const known = Object.prototype.hasOwnProperty.call(KNOWN_OUTCOMES, outcome)
    ? // eslint-disable-next-line security/detect-object-injection -- own-property guard above
      KNOWN_OUTCOMES[outcome]
    : undefined;
  if (known) return known;

  // Fallback: coarse substring match for unknown firmware-emitted outcomes.
  const lower = outcome.toLowerCase();
  if (
    lower.includes('warning') ||
    lower.includes('stopped') ||
    lower.includes('interrupted')
  ) {
    return { icon: 'event-warning', colorClassName: 'color-orange-500' };
  }
  if (lower.includes('abort') || lower.includes('error') || lower.includes('fail')) {
    return { icon: 'event-error', colorClassName: 'color-red-500' };
  }
  return FALLBACK;
}
